import { useState, useEffect } from "react";
import { useCreateUOM, useOneUOM, useUpdateUOM } from "http-hooks/uom/useUOM";
import { useAllUnits } from "http-hooks/unit/useUnit";
import { Switch } from "@headlessui/react";
import Select from "components/ui/Select";
import Input from "components/ui/Input";
import Modal from "components/ui/modals";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function SetUOM({ id, onClose, open }) {
  const [uomDetails, setUomDetails] = useState({
    itemuom: "",
    conval: 0,
    itembasicuom: "",
    ag_status: 1,
  });
  const [selectedBillUnit, setSelectedBillUnit] = useState(null);
  const [selectedItemUnit, setSelectedItemUnit] = useState(null);

  const { isLoading: isAllUnitsLoading, data: { data: allunits } = [] } = useAllUnits({
    unitname: "",
    systemg: "",
  });

  const { isLoading: isLoadingUOM, data } = useOneUOM(id);
  const { mutateAsync: mutateCreate, isPending: isCreating } = useCreateUOM();
  const { mutateAsync: mutateUpdate, isPending: isUpdating } = useUpdateUOM();

  const isPending = isCreating || isUpdating;

  const unitIdOfBillUnit = allunits?.find((unit) => unit.unitname === "BILLUNIT")?.unitId;
  const billUnitOptions = allunits
    ?.filter((unit) => unit.unitidunder === unitIdOfBillUnit)
    ?.map((unit) => ({ value: unit.unitname, label: unit.unitname, ...unit })) || [];

  const unitIdOfItemUnit = allunits?.find((unit) => unit.unitname === "ITEMUNIT")?.unitId;
  const itemUnitOptions = allunits
    ?.filter((unit) => unit.unitidunder === unitIdOfItemUnit)
    ?.map((unit) => ({ value: unit.unitname, label: unit.unitname, ...unit })) || [];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUomDetails((prevData) => ({ ...prevData, [name]: value }));
  };

  useEffect(() => {
    if (selectedBillUnit) {
      setUomDetails((prev) => ({ ...prev, itemuom: selectedBillUnit.value }));
    }
  }, [selectedBillUnit]);

  useEffect(() => {
    if (selectedItemUnit) {
      setUomDetails((prev) => ({ ...prev, itembasicuom: selectedItemUnit.value }));
    }
  }, [selectedItemUnit]);

  useEffect(() => {
    if (data && allunits) {
      setUomDetails((prev) => {
        const updatedDetails = {};
        Object.keys(prev).forEach((key) => {
          updatedDetails[key] = data[key] ?? prev[key];
        });
        return updatedDetails;
      });
      if (data.itemuom) {
        const billUnit = allunits?.find((unit) => unit.unitname === data.itemuom);
        if (billUnit) setSelectedBillUnit({ value: billUnit.unitname, label: billUnit.unitname, ...billUnit });
      }
      if (data.itembasicuom) {
        const itemUnit = allunits?.find((unit) => unit.unitname === data.itembasicuom);
        if (itemUnit) setSelectedItemUnit({ value: itemUnit.unitname, label: itemUnit.unitname, ...itemUnit });
      }
    }
  }, [data, allunits]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (id) {
        await mutateUpdate({ id, data: uomDetails });
      } else {
        await mutateCreate(uomDetails);
      }
      onClose();
    } catch (error) {
      console.error("Operation failed:", error);
    }
  };

  return (
    <Modal
      open={open}
      setOpen={(val) => !val && onClose()}
      header={
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          {id ? "Update" : "Add"} UOM Conversion
        </h3>
      }
      footer={
        <div className="flex justify-end gap-x-3">
          <button
            type="button"
            className="text-sm font-semibold rounded-md px-3 py-2 hover:bg-gray-100 text-gray-900"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="set-uom-form"
            disabled={isPending}
            className="rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Saving..." : "Save"}
          </button>
        </div>
      }
    >
      {id && isLoadingUOM ? (
        <div className="animate-pulse space-y-6">
          <div className="h-9 bg-gray-200 rounded w-full" />
          <div className="h-9 bg-gray-200 rounded w-full" />
          <div className="h-9 bg-gray-200 rounded w-full" />
        </div>
      ) : (
        <form id="set-uom-form" onSubmit={handleSubmit} className="space-y-6">
          <Select
            options={billUnitOptions}
            value={selectedBillUnit}
            onChange={setSelectedBillUnit}
            placeholder="Select Bill Unit"
            label="Bill Unit"
            isLoading={isAllUnitsLoading}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
          <Input
            type="number"
            name="conval"
            id="conval"
            label="Conversion Ratio"
            value={uomDetails.conval}
            onChange={handleInputChange}
            step="0.01"
            min="0"
          />
          <Select
            options={itemUnitOptions}
            value={selectedItemUnit}
            onChange={setSelectedItemUnit}
            placeholder="Select Item Unit"
            label="Item Unit (Actual)"
            isLoading={isAllUnitsLoading}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
          {id && (
            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900">
                Status
              </label>
              <Switch
                checked={!!uomDetails.ag_status}
                onChange={(e) => setUomDetails((prevData) => ({ ...prevData, ag_status: e ? 1 : 0 }))}
                className={classNames(
                  uomDetails.ag_status ? "bg-indigo-600" : "bg-gray-200",
                  "relative inline-flex ml-1 mt-3 h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2"
                )}
              >
                <span
                  aria-hidden="true"
                  className={classNames(
                    uomDetails.ag_status ? "translate-x-5" : "translate-x-0",
                    "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                  )}
                />
              </Switch>
            </div>
          )}
        </form>
      )}
    </Modal>
  );
}
